import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Zap, Mail, Lock, User, Loader2 } from "lucide-react";
import { useAuth, formatApiError } from "@/context/AuthContext";

const GOOGLE_AUTH_URL = "https://auth.emergentagent.com/";

export default function Login() {
  const { user, login, register, googleSession } = useAuth();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/";

  // Handle Google redirect (#session_id=...)
  useEffect(() => {
    const hash = window.location.hash;
    if (hash.includes("session_id=")) {
      const sid = new URLSearchParams(hash.replace("#", "")).get("session_id");
      if (sid) {
        setLoading(true);
        googleSession(sid)
          .then(() => {
            window.location.hash = "";
            toast.success("Signed in with Google");
            navigate("/", { replace: true });
          })
          .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
          .finally(() => setLoading(false));
      }
    }
  }, [googleSession, navigate]);

  useEffect(() => {
    if (user) navigate(from, { replace: true });
  }, [user, navigate, from]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        await login(email, password);
        toast.success("Welcome back");
      } else {
        await register(email, password, name);
        toast.success("Account created");
      }
      navigate(from, { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = () => {
    const redirect = `${window.location.origin}/login`;
    window.location.href = `${GOOGLE_AUTH_URL}?redirect=${encodeURIComponent(redirect)}`;
  };

  return (
    <div className="min-h-screen flex" style={{ background: "var(--pk-bg)" }}>
      {/* Left banner */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden border-r" style={{ borderColor: "var(--pk-border)" }}>
        <img
          src="https://images.unsplash.com/photo-1613771404784-3a5686aa2be3?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Trading cards"
          className="absolute inset-0 w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, rgba(9,11,16,0.7), rgba(9,11,16,0.95))" }} />
        <div className="relative z-10 flex flex-col justify-end p-14">
          <div className="holo-shine inline-flex w-12 h-12 rounded-xl items-center justify-center border mb-6" style={{ borderColor: "var(--pk-border)" }}>
            <Zap className="h-6 w-6 text-amber-400" />
          </div>
          <h1 className="font-heading text-4xl font-extrabold text-slate-50 leading-tight">
            One inventory.<br />Every channel.
          </h1>
          <p className="text-slate-400 mt-4 max-w-md text-sm leading-relaxed">
            Sync your Pokémon, Magic, One Piece, Lorcana, Yu-Gi-Oh! and more across Shopify and TCGPlayer. Sell once, decrement everywhere — never double-sell a card again.
          </p>
          <div className="flex gap-3 mt-8">
            <span className="px-3 py-1.5 rounded-full text-xs font-medium bg-emerald-950/80 text-emerald-400 border border-emerald-800/60">Shopify Live Sync</span>
            <span className="px-3 py-1.5 rounded-full text-xs font-medium bg-blue-950/80 text-blue-400 border border-blue-800/60">TCGPlayer CSV</span>
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md animate-fade-up">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <Zap className="h-6 w-6 text-amber-400" />
            <span className="font-heading font-extrabold text-xl text-slate-100">TCG Vendor Hub</span>
          </div>
          <h2 className="font-heading text-2xl font-bold text-slate-100">
            {mode === "login" ? "Sign in to your vault" : "Create your account"}
          </h2>
          <p className="text-sm text-slate-400 mt-1 mb-8">
            {mode === "login" ? "Manage your TCG inventory across channels." : "Start syncing your card inventory."}
          </p>

          <button
            data-testid="auth-google-button"
            onClick={handleGoogle}
            className="w-full flex items-center justify-center gap-3 py-3 rounded-xl border text-sm font-medium text-slate-200 hover:bg-[var(--pk-hover)] transition-colors mb-5"
            style={{ background: "var(--pk-card)", borderColor: "var(--pk-border)" }}
          >
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="h-5 w-5" />
            Continue with Google
          </button>

          <div className="flex items-center gap-3 mb-5">
            <div className="h-px flex-1" style={{ background: "var(--pk-border)" }} />
            <span className="text-xs text-slate-500">or</span>
            <div className="h-px flex-1" style={{ background: "var(--pk-border)" }} />
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === "signup" && (
              <Field icon={User} label="Name">
                <input value={name} onChange={(e) => setName(e.target.value)}
                  data-testid="auth-name-input" placeholder="Your name"
                  className="w-full bg-transparent outline-none text-sm text-slate-100 placeholder:text-slate-600" />
              </Field>
            )}
            <Field icon={Mail} label="Email">
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid="auth-email-input" placeholder="you@example.com"
                className="w-full bg-transparent outline-none text-sm text-slate-100 placeholder:text-slate-600" />
            </Field>
            <Field icon={Lock} label="Password">
              <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                data-testid="auth-password-input" placeholder="••••••••"
                className="w-full bg-transparent outline-none text-sm text-slate-100 placeholder:text-slate-600" />
            </Field>
            <button
              type="submit" disabled={loading}
              data-testid="auth-login-button"
              className="w-full flex items-center justify-center gap-2 py-3 rounded-xl font-semibold text-sm bg-amber-500 text-[#1a1205] hover:bg-amber-400 transition-colors disabled:opacity-60"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              {mode === "login" ? "Sign in" : "Create account"}
            </button>
          </form>

          <p className="text-sm text-slate-400 mt-6 text-center">
            {mode === "login" ? "New here? " : "Already have an account? "}
            <button
              data-testid="auth-toggle-signup"
              onClick={() => setMode(mode === "login" ? "signup" : "login")}
              className="text-amber-400 font-medium hover:underline"
            >
              {mode === "login" ? "Create an account" : "Sign in"}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}

function Field({ icon: Icon, label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">{label}</span>
      <div className="mt-1.5 flex items-center gap-2 px-3 py-2.5 rounded-xl border focus-within:ring-2 focus-within:ring-amber-500/50 transition-all"
           style={{ background: "var(--pk-card)", borderColor: "var(--pk-border)" }}>
        <Icon className="h-4 w-4 text-slate-500 shrink-0" />
        {children}
      </div>
    </label>
  );
}
